import React, { useState, useEffect, useCallback, useMemo } from 'react';
import sortByPrice from 'utils/sortByPrice';
import { isFirstPage } from '../utils/helpers';
import ProductCard from '../product-card/product-card';
import Sort from './sort';
import Filter from './filter';
import { SHOPIFY_SPECIFIC_PARAMS, SORT_BY_PRICE_LOW_TO_HIGH, SORT_BY_PRICE_HIGH_TO_LOW } from './constants';
import IconClose from '../utils/icon/icon-close.svg';
import MarketingCard from './marketing-card';

const locales = window.TMW.Strings;
const facetLocales = locales.collection.facets;

const ProductGrid = (props) => {
  const [products, setProducts] = useState(props.products || []);
  const [sort, setSort] = useState(props.sort || {});
  const [filter, setFilter] = useState(props.filter || {});
  const [appliedFilterOptions, setAppliedFilterOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hideCards, setHideCards] = useState(true);
  const onSale = props.collectionSale;
  const cards = useMemo(() => props.cards || [], [props?.cards]);
  const cardConfig = useMemo(() => props.cardLayout, [props?.cardLayout]);
  const isSortingByPrice = (value) => value === SORT_BY_PRICE_LOW_TO_HIGH || value === SORT_BY_PRICE_HIGH_TO_LOW;
  localStorage.setItem("first_visit", 0);

  useEffect(() => {
    const getParams = () => {
      const searchParams = new URLSearchParams(window.location.search);
      const newAppliedFilters = Array.from(searchParams.entries())
        .filter(
          ([paramName]) =>
            paramName !== SHOPIFY_SPECIFIC_PARAMS.view &&
            paramName !== SHOPIFY_SPECIFIC_PARAMS.sortBy &&
            isFilterParam(paramName),
        )
        .map(([paramName, value]) => ({ value, paramName, urlToAdd: '', urlToRemove: '' }));

      setAppliedFilterOptions(newAppliedFilters);
    };

    getParams();

    const sortByValue = new URLSearchParams(window.location.search).get(SHOPIFY_SPECIFIC_PARAMS.sortBy);

    if (isSortingByPrice(sortByValue) && sortByValue) {
      const sortedByPriceProducts = sortByPrice(products, sortByValue);
      setProducts(sortedByPriceProducts);
    }
  }, []);

  useEffect(() => {
    if (appliedFilterOptions.length === 0) return;
    const filterChangeEvent = new CustomEvent('plp:filtered', {
      detail: {
        appliedFilterOptions,
      },
    });

    window.dispatchEvent(filterChangeEvent);
  }, [appliedFilterOptions]);

  const isFilterParam = (paramName) => {
    return paramName.startsWith('filter');
  };

  const updateURLParam = ({ key, value, remove = false, removeAll = false, replace = false }) => {
    const url = new URL(window.location);
    const searchParams = new URLSearchParams(url.search);

    if (removeAll) {
      const preservedParams = new URLSearchParams();
      searchParams.forEach((val, paramKey) => {
        if (paramKey === SHOPIFY_SPECIFIC_PARAMS.sortBy) {
          preservedParams.append(paramKey, val);
        }
      });
      url.search = preservedParams.toString();
    } else if (!value && remove) {
      searchParams.delete(key);
      url.search = searchParams.toString();
    } else if (value && remove) {
      const currentValues = searchParams.getAll(key).filter((val) => val !== value);
      searchParams.delete(key);
      currentValues.forEach((val) => searchParams.append(key, val));
      url.search = searchParams.toString();
    } else if (replace) {
      searchParams.set(key, value);
      url.search = searchParams.toString();
    } else {
      searchParams.append(key, value);
      url.search = searchParams.toString();
    }

    window.history.replaceState({}, '', url.toString());
  };

  const getCollectionBySort = useCallback(
    async (sortOption) => {
      const path = window.location.pathname;
      const { view, sortBy } = SHOPIFY_SPECIFIC_PARAMS;

      const filterOption = appliedFilterOptions.length
        ? appliedFilterOptions.map((option) => `&${option.paramName}=${option.value}`).join('')
        : '';
      const route = `${path}?${view}=api&${sortBy}=${sortOption}${filterOption}`;

      try {
        const res = await fetch(route);
        if (!res.ok) throw new Error('Network response was not ok');

        const data = await res.json();

        if (!data) {
          throw new Error('No data was returned');
        }

        return data;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        return null;
      }
    },
    [appliedFilterOptions],
  );

  const getCollectionByFilter = async (filterParams = '') => {
    const path = window.location.pathname;
    const { view, sortBy } = SHOPIFY_SPECIFIC_PARAMS;

    const sortOption = sort.current ? `${sortBy}=${sort.current}&` : '';
    const route = `${path}?${view}=api&${sortOption}${filterParams}`;

    try {
      const res = await fetch(route);
      if (!res.ok) throw new Error('Network response was not ok');

      const data = await res.json();

      if (!data) {
        throw new Error('No data was returned');
      }

      return data;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      return null;
    }
  };

  const handleSortChange = useCallback(
    async (eOrValue, cardsHidden = true) => {
      const value = eOrValue.target ? eOrValue.target.value : eOrValue;
      const data = await getCollectionBySort(value);
      if (!data) return;

      if (isSortingByPrice(value)) {
        const sortedByPriceProducts = sortByPrice(data.products, value);
        setProducts(sortedByPriceProducts);
      } else {
        setProducts(data.products);
      }
      setSort(data.sort);
      setHideCards(cardsHidden);

      updateURLParam({ key: SHOPIFY_SPECIFIC_PARAMS.sortBy, value, replace: true });
    },
    [getCollectionBySort],
  );

  useEffect(() => {
    if (appliedFilterOptions.length > 0) {
      setHideCards(true);
    } else {
      setHideCards(false);
    }
  }, [appliedFilterOptions]);

  const handleFilterChange = useCallback(
    async (e, { value, paramName, urlToAdd, urlToRemove }) => {
      if (isLoading) return;

      setIsLoading(true);

      const existingOption = appliedFilterOptions.some((option) => option.value === value);
      if (existingOption) {
        await handleRemoveFilter(value, paramName);
        setIsLoading(false);
        return;
      }
      const getUrl = urlToAdd.replace(`/collections/${props.handle}?`, '');
      const data = await getCollectionByFilter(getUrl);
      if (!data) {
        setIsLoading(false);
        return;
      }

      const sortByValue = new URLSearchParams(window.location.search).get(SHOPIFY_SPECIFIC_PARAMS.sortBy);

      if (isSortingByPrice(sortByValue)) {
        const sortedByPriceProducts = sortByPrice(data.products, sortByValue);
        setProducts(sortedByPriceProducts);
      } else {
        setProducts(data.products);
      }

      setFilter(data.filter);

      updateURLParam({ key: `${paramName}`, value });

      setAppliedFilterOptions((prevState) => {
        const filterExists = prevState.some(
          (appliedFilterList) => appliedFilterList.value === value && appliedFilterList.paramName === paramName,
        );
        if (!filterExists) {
          return [...prevState, { value, paramName, urlToAdd, urlToRemove }];
        }
        return prevState;
      });

      setIsLoading(false);
    },
    [appliedFilterOptions, isLoading],
  );

  const handleRemoveFilter = async (value, paramName) => {
    updateURLParam({ key: paramName, value, remove: true });

    const updatedSearchParams = new URLSearchParams(window.location.search).toString();
    const data = await getCollectionByFilter(updatedSearchParams);
    if (!data) return;

    const sortByValue = new URLSearchParams(window.location.search).get(SHOPIFY_SPECIFIC_PARAMS.sortBy);

    if (isSortingByPrice(sortByValue)) {
      const sortedByPriceProducts = sortByPrice(data.products, sortByValue);
      setProducts(sortedByPriceProducts);
    } else {
      setProducts(data.products);
    }

    setFilter(data.filter);

    setAppliedFilterOptions((prevState) =>
      prevState.filter(
        (appliedFilterList) => appliedFilterList.value !== value || appliedFilterList.paramName !== paramName,
      ),
    );
  };

  const handleClearFilters = useCallback(async (sortOption) => {
    setAppliedFilterOptions([]);
    updateURLParam({ removeAll: true });

    const data = await getCollectionByFilter('');
    if (data) {
      const sortByValue = new URLSearchParams(window.location.search).get(SHOPIFY_SPECIFIC_PARAMS.sortBy);

      if (isSortingByPrice(sortByValue)) {
        const sortedByPriceProducts = sortByPrice(data.products, sortByValue);
        setProducts(sortedByPriceProducts);
      } else {
        setProducts(data.products);
      }
      setFilter(data.filter);

      if (sortOption.current) {
        handleSortChange(sortOption.current, false);
      }
    }
  }, []);

  const orderedCards = useMemo(() => {
    if (!products.length) return false;
    if (!isFirstPage() || hideCards) return products.filter((product) => !product?.isCard);
    const marketingCards = cards.map((card, index) => {
      return { ...card, position: cardConfig[index] ?? 9999999 };
    });
    const newProductAndCardArray = products;
    marketingCards.sort((a, b) => a.position - b.position);

    marketingCards.forEach((card) => {
      newProductAndCardArray.splice(card.position - 1, 0, { ...card, isCard: true });
    });

    return newProductAndCardArray;
  }, [products, cards, cardConfig, hideCards]);

  return (
    orderedCards && (
      <div className="px-3 pb-8 md:px-8">
        {!props.hide_filters && (
          <div className="my-5 grid gap-y-4">
            <div className="flex items-center justify-between">
              <Filter
                {...filter}
                onChange={handleFilterChange}
                handleClearFilters={() => handleClearFilters(sort)}
                disableApplyButtonState={appliedFilterOptions.length > 0}
                isLoading={isLoading}
              />
              <Sort
                {...sort}
                onChange={handleSortChange}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {appliedFilterOptions.map((appliedFilter) => (
                <button
                  key={appliedFilter.value}
                  onClick={() => handleRemoveFilter(appliedFilter.value, appliedFilter.paramName)}
                  value={appliedFilter.value}
                  data-param-name={appliedFilter.paramName}
                  data-url-to-remove={appliedFilter.urlToRemove}
                  className="body-3 flex items-center gap-x-[2px] rounded-full bg-brand-light-beige px-2 [&>svg]:size-3"
                >
                  <span>{appliedFilter.value}</span>
                  <IconClose />
                </button>
              ))}
              {appliedFilterOptions.length > 0 && (
                <button
                  className="text-[14px] font-medium underline underline-offset-2"
                  onClick={() => handleClearFilters(sort)}
                >
                  {facetLocales.clearAll}
                </button>
              )}
            </div>
          </div>
        )}
        <div className="mb-8 grid grid-cols-1 gap-7.5 md:grid-cols-2 lg:grid-cols-4">
          {orderedCards?.map((item, index) =>
            item?.isCard ? (
              <MarketingCard
                key={index}
                card={item}
              />
            ) : (
              <ProductCard
                key={item.id}
                product={item}
                placement="plp"
                onSale={onSale}
                inputPrefix={props.handle}
                {...props}
              />
            ),
          )}
        </div>
      </div>
    )
  );
};

export default ProductGrid;
